import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAPIUrl } from "../api/apiUtils";
import { apiFetch } from "../api/apiClient";
import "./TableReportsBuilder.scss";

const API_BASE = getAPIUrl();

const OP_LABELS = {
  eq: "=",
  ne: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  between: "между",
  in: "в списке",
  contains: "содержит",
  startswith: "начинается с",
  endswith: "заканчивается на",
  is_null: "пусто",
  is_not_null: "не пусто",
  json_path_eq: "JSON путь =",
  json_path_contains: "JSON путь содержит",
  json_path_exists: "JSON путь существует",
};

const newBlockId = () => `b_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const emptyBlock = () => ({
  id: newBlockId(),
  sourceId: "",
  schema: null,
  selectedColumns: [],
  filters: [],
  sortRows: [{ uid: "s0", field: "", direction: "desc" }],
  limit: 50,
  offset: 0,
  preview: null,
  jsonPathsByField: {},
  loadingSchema: false,
  loadingPreview: false,
});

const newFilterRow = () => ({
  uid: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  field: "",
  op: "",
  valueSingle: "",
  valueBetweenFrom: "",
  valueBetweenTo: "",
  valueIn: "",
  jsonPath: "",
});

const parseInValues = (text, column) => {
  const parts = String(text || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.length) return [];
  const t = column?.type || "";
  if (t === "integer" || t === "int") return parts.map((p) => parseInt(p, 10)).filter((n) => !Number.isNaN(n));
  if (t === "float" || t === "numeric" || t === "number") return parts.map((p) => parseFloat(p)).filter((n) => !Number.isNaN(n));
  return parts;
};

const coerceScalar = (column, raw) => {
  const t = column?.type || "";
  if (t === "integer" || t === "int") {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? raw : n;
  }
  if (t === "float" || t === "numeric" || t === "number") {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? raw : n;
  }
  if (t === "boolean" || t === "bool") return raw === true || raw === "true" || raw === "1";
  return raw;
};

const buildFilterObjects = (schema, rows, quickGroupId) => {
  if (!schema?.columns) return [];
  const byName = Object.fromEntries(schema.columns.map((c) => [c.name, c]));
  const out = [];

  const gid = String(quickGroupId || "").trim();
  const groupColumn = byName[schema.group_field];
  if (gid && groupColumn) {
    const numeric = groupColumn.type === "integer" || groupColumn.type === "number";
    if (!numeric || /^-?\d+$/.test(gid)) {
      out.push({ field: schema.group_field, op: "eq", value: numeric ? Number(gid) : gid });
    }
  }

  for (const row of rows) {
    const col = byName[row.field];
    if (!col || !row.op) continue;
    if (!col.operators?.includes(row.op)) continue;

    if (row.op === "is_null" || row.op === "is_not_null") {
      out.push({ field: row.field, op: row.op });
      continue;
    }

    const jsonOps = ["json_path_eq", "json_path_contains", "json_path_exists"];
    if (jsonOps.includes(row.op)) {
      if (!row.jsonPath?.trim()) continue;
      const f = { field: row.field, json_path: row.jsonPath.trim(), op: row.op };
      if (row.op !== "json_path_exists") {
        if (row.valueSingle === "" && row.valueSingle !== 0 && row.valueSingle !== false) continue;
        f.value = row.valueSingle;
      }
      out.push(f);
      continue;
    }

    if (row.op === "between") {
      if (!row.valueBetweenFrom || !row.valueBetweenTo) continue;
      out.push({
        field: row.field,
        op: "between",
        value: [row.valueBetweenFrom, row.valueBetweenTo],
      });
      continue;
    }

    if (row.op === "in") {
      const arr = parseInValues(row.valueIn, col);
      if (!arr.length) continue;
      out.push({ field: row.field, op: "in", value: arr });
      continue;
    }

    if (row.valueSingle === "" && row.valueSingle !== 0 && row.valueSingle !== false) continue;
    out.push({ field: row.field, op: row.op, value: coerceScalar(col, row.valueSingle) });
  }

  return out;
};

const buildSortObjects = (schema, sortRows) => {
  if (!schema?.columns) return [];
  const sortable = new Set(schema.columns.filter((c) => c.sortable).map((c) => c.name));
  return sortRows
    .filter((s) => s.field && sortable.has(s.field))
    .map((s) => ({ field: s.field, direction: s.direction === "asc" ? "asc" : "desc" }));
};

const buildBlockPayload = (block, periodFrom, periodTo, quickGroupId) => {
  const { sourceId, schema, selectedColumns, filters, sortRows, limit, offset } = block;
  const cols = selectedColumns.filter(Boolean);
  const payload = {
    source: sourceId,
    columns: cols.length ? cols : undefined,
    filters: buildFilterObjects(schema, filters, quickGroupId),
    sort: buildSortObjects(schema, sortRows),
    limit,
    offset,
  };
  if (schema?.supports_period && (periodFrom || periodTo)) {
    payload.period = { from: periodFrom || undefined, to: periodTo || undefined };
  }
  return payload;
};

function TableReportBlocksPanel() {
  const [sources, setSources] = useState([]);
  const [blocks, setBlocks] = useState([emptyBlock()]);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [quickGroupId, setQuickGroupId] = useState("");
  const [reportTitle, setReportTitle] = useState("Отчёт");
  const [truncateCell, setTruncateCell] = useState(300);
  const [loadingSources, setLoadingSources] = useState(true);
  const [loadingRender, setLoadingRender] = useState(false);
  const [error, setError] = useState(null);

  const anyPeriod = useMemo(
    () => blocks.some((b) => b.schema?.supports_period),
    [blocks]
  );
  const anyGroup = useMemo(() => blocks.some((b) => b.schema?.group_field), [blocks]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSources(true);
      setError(null);
      try {
        const res = await apiFetch(`${API_BASE}/reports/sources`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (cancelled) return;
        setSources(Array.isArray(data.sources) ? data.sources : []);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoadingSources(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateBlock = useCallback((id, patch) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }, []);

  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  const schemaKey = blocks.map((b) => `${b.id}:${b.sourceId}`).join("|");

  useEffect(() => {
    const cancels = [];
    const list = blocksRef.current;
    list.forEach((block) => {
      if (!block.sourceId) {
        if (block.schema) {
          updateBlock(block.id, { schema: null, preview: null, selectedColumns: [], filters: [] });
        }
        return;
      }
      let cancelled = false;
      cancels.push(() => {
        cancelled = true;
      });
      (async () => {
        updateBlock(block.id, { loadingSchema: true });
        try {
          const res = await apiFetch(
            `${API_BASE}/reports/sources/${encodeURIComponent(block.sourceId)}/schema`
          );
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${res.status}`);
          }
          const data = await res.json();
          if (cancelled) return;
          const defaults = Array.isArray(data.default_columns) ? [...data.default_columns] : [];
          const selectedColumns = defaults.length
            ? defaults
            : (data.columns || []).map((c) => c.name).slice(0, 8);
          const dateCol = data.columns?.find((c) => c.name === data.date_field && c.sortable);
          const sortRows = [
            {
              uid: "s0",
              field: dateCol?.name || (data.columns?.find((c) => c.sortable)?.name ?? ""),
              direction: "desc",
            },
          ];
          updateBlock(block.id, {
            schema: data,
            selectedColumns,
            sortRows,
            filters: [],
            preview: null,
            offset: 0,
            jsonPathsByField: {},
            loadingSchema: false,
          });
        } catch (e) {
          if (!cancelled) {
            setError(e.message || String(e));
            updateBlock(block.id, { schema: null, loadingSchema: false });
          }
        }
      })();
    });
    return () => {
      cancels.forEach((fn) => fn());
    };
  }, [schemaKey, updateBlock]);

  const addBlock = () => setBlocks((prev) => [...prev, emptyBlock()]);
  const removeBlock = (id) => setBlocks((prev) => (prev.length <= 1 ? prev : prev.filter((b) => b.id !== id)));

  const runPreviewBlock = async (blockSnapshot, nextOffset) => {
    if (!blockSnapshot?.sourceId || !blockSnapshot.schema) return;
    updateBlock(blockSnapshot.id, { loadingPreview: true });
    setError(null);
    try {
      const body = {
        ...buildBlockPayload(blockSnapshot, periodFrom, periodTo, quickGroupId),
        offset: nextOffset,
      };
      const res = await apiFetch(`${API_BASE}/reports/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      updateBlock(blockSnapshot.id, { preview: data, offset: nextOffset, loadingPreview: false });
    } catch (e) {
      setError(e.message || String(e));
      updateBlock(blockSnapshot.id, { preview: null, loadingPreview: false });
    }
  };

  const previewAll = async () => {
    for (const b of blocks) {
      if (b.sourceId && b.schema) {
        await runPreviewBlock(b, 0);
      }
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadRenderAll = async (format) => {
    const ready = blocks.filter((b) => b.sourceId && b.schema);
    if (!ready.length) {
      setError("Добавьте хотя бы одну таблицу с выбранным источником.");
      return;
    }
    setLoadingRender(true);
    setError(null);
    try {
      for (let i = 0; i < ready.length; i++) {
        const b = ready[i];
        const payload = {
          ...buildBlockPayload(b, periodFrom, periodTo, quickGroupId),
          format,
          title: `${reportTitle || "Отчёт"} · ${b.schema?.title || b.sourceId}`,
          options: { truncate_cell_length: Number(truncateCell) || 300 },
        };
        delete payload.offset;
        const res = await apiFetch(`${API_BASE}/reports/render`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const ct = res.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${res.status}`);
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const blob = await res.blob();
        const safe = String(b.sourceId).replace(/[^\w.-]+/g, "_");
        const ext = format === "pdf" ? "pdf" : "docx";
        downloadBlob(blob, `${i + 1}_${safe}.${ext}`);
        await new Promise((r) => setTimeout(r, 350));
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoadingRender(false);
    }
  };

  const loadJsonPaths = async (blockId, sourceId, fieldName) => {
    if (!sourceId || !fieldName) return;
    setError(null);
    try {
      const url = `${API_BASE}/reports/sources/${encodeURIComponent(
        sourceId
      )}/json-fields?field=${encodeURIComponent(fieldName)}`;
      const res = await apiFetch(url);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setBlocks((prev) =>
        prev.map((b) => {
          if (b.id !== blockId) return b;
          return {
            ...b,
            jsonPathsByField: { ...b.jsonPathsByField, [fieldName]: data.paths || [] },
          };
        })
      );
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  return (
    <div className="table-reports">
      <h2 className="table-reports__title">Табличные отчёты (несколько источников)</h2>
      <p className="table-reports__meta">
        Каждая таблица — отдельный источник и набор колонок. Период и фильтр по группе задаются общими для всех блоков,
        если соответствующая схема это поддерживает. Выгрузка PDF/DOCX: по одному файлу на таблицу (API пока принимает
        один source за запрос).
      </p>

      {error && <div className="table-reports__error">{error}</div>}

      <div className="table-reports__toolbar">
        {anyPeriod && (
          <>
            <div className="table-reports__field">
              <label htmlFor="tr-from">Период с</label>
              <input
                id="tr-from"
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
              />
            </div>
            <div className="table-reports__field">
              <label htmlFor="tr-to">по</label>
              <input id="tr-to" type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
            </div>
          </>
        )}
        {anyGroup && (
          <div className="table-reports__field">
            <label htmlFor="tr-group">Быстрый фильтр: ID группы</label>
            <input
              id="tr-group"
              type="text"
              inputMode="numeric"
              placeholder="например 12345"
              value={quickGroupId}
              onChange={(e) => setQuickGroupId(e.target.value)}
            />
          </div>
        )}
        <button type="button" className="table-reports__btn" onClick={addBlock}>
          + Таблица
        </button>
      </div>

      {blocks.map((block, idx) => {
        const srcMeta = sources.find((s) => s.id === block.sourceId);
        const schema = block.schema;
        const preview = block.preview;
        const previewColumns = preview?.columns || [];
        const previewRows = preview?.rows || [];

        const patchFilter = (uid, patch) =>
          updateBlock(block.id, {
            filters: block.filters.map((f) => (f.uid === uid ? { ...f, ...patch } : f)),
          });
        const addFilter = () =>
          updateBlock(block.id, { filters: [...block.filters, newFilterRow()] });
        const removeFilter = (uid) =>
          updateBlock(block.id, { filters: block.filters.filter((f) => f.uid !== uid) });

        const patchSort = (uid, patch) =>
          updateBlock(block.id, {
            sortRows: block.sortRows.map((s) => (s.uid === uid ? { ...s, ...patch } : s)),
          });
        const addSort = () =>
          updateBlock(block.id, {
            sortRows: [
              ...block.sortRows,
              {
                uid: `s_${Date.now()}`,
                field: schema?.columns?.find((c) => c.sortable)?.name ?? "",
                direction: "desc",
              },
            ],
          });
        const removeSort = (uid) =>
          updateBlock(block.id, {
            sortRows: block.sortRows.length <= 1 ? block.sortRows : block.sortRows.filter((s) => s.uid !== uid),
          });

        const toggleColumn = (name) => {
          const set = new Set(block.selectedColumns);
          if (set.has(name)) set.delete(name);
          else set.add(name);
          updateBlock(block.id, { selectedColumns: Array.from(set) });
        };

        return (
          <section key={block.id} className="table-reports__section table-reports__block-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <h3 style={{ margin: 0 }}>Таблица {idx + 1}</h3>
              <button
                type="button"
                className="table-reports__btn table-reports__btn--danger"
                disabled={blocks.length <= 1}
                onClick={() => removeBlock(block.id)}
              >
                Удалить блок
              </button>
            </div>

            <div className="table-reports__toolbar">
              <div className="table-reports__field">
                <label>Источник данных</label>
                <select
                  value={block.sourceId}
                  onChange={(e) => updateBlock(block.id, { sourceId: e.target.value })}
                  disabled={loadingSources}
                >
                  <option value="">{loadingSources ? "Загрузка…" : "— выберите —"}</option>
                  {sources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title || s.id}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {block.loadingSchema && <div className="table-reports__meta">Загрузка схемы…</div>}

            {schema && (
              <>
                <section className="table-reports__section">
                  <h4>Колонки</h4>
                  <div className="table-reports__columns">
                    {schema.columns?.map((col) => (
                      <label key={col.name} className="table-reports__column-item">
                        <input
                          type="checkbox"
                          checked={block.selectedColumns.includes(col.name)}
                          onChange={() => toggleColumn(col.name)}
                        />
                        <span>{col.label || col.name}</span>
                        <span className="table-reports__meta">({col.type})</span>
                      </label>
                    ))}
                  </div>
                </section>

                <section className="table-reports__section">
                  <h4>Фильтры</h4>
                  {block.filters.map((row) => {
                    const col = schema.columns?.find((c) => c.name === row.field);
                    const ops = col?.operators || [];
                    const isJsonOp = ["json_path_eq", "json_path_contains", "json_path_exists"].includes(row.op);
                    return (
                      <div key={row.uid} className="table-reports__row">
                        <select
                          value={row.field}
                          onChange={(e) => {
                            const next = schema.columns?.find((c) => c.name === e.target.value);
                            patchFilter(row.uid, {
                              field: e.target.value,
                              op: next?.operators?.[0] || "",
                            });
                          }}
                        >
                          <option value="">поле…</option>
                          {schema.columns
                            ?.filter((c) => c.filterable)
                            .map((c) => (
                              <option key={c.name} value={c.name}>
                                {c.label || c.name}
                              </option>
                            ))}
                        </select>
                        <select
                          value={row.op}
                          onChange={(e) => patchFilter(row.uid, { op: e.target.value })}
                          disabled={!col}
                        >
                          <option value="">оператор…</option>
                          {ops.map((op) => (
                            <option key={op} value={op}>
                              {OP_LABELS[op] || op}
                            </option>
                          ))}
                        </select>
                        {isJsonOp && (
                          <>
                            <select
                              value={row.jsonPath}
                              onChange={(e) => patchFilter(row.uid, { jsonPath: e.target.value })}
                            >
                              <option value="">JSON-путь…</option>
                              {(block.jsonPathsByField[row.field] || []).map((p) => (
                                <option key={p.path} value={p.path}>
                                  {p.path} ({p.type})
                                </option>
                              ))}
                            </select>
                            {srcMeta?.has_json_fields && row.field && (
                              <button
                                type="button"
                                className="table-reports__btn"
                                onClick={() => loadJsonPaths(block.id, block.sourceId, row.field)}
                              >
                                Загрузить пути
                              </button>
                            )}
                          </>
                        )}
                        {row.op === "between" && (
                          <>
                            <input
                              type="text"
                              placeholder="от"
                              value={row.valueBetweenFrom}
                              onChange={(e) => patchFilter(row.uid, { valueBetweenFrom: e.target.value })}
                            />
                            <input
                              type="text"
                              placeholder="до"
                              value={row.valueBetweenTo}
                              onChange={(e) => patchFilter(row.uid, { valueBetweenTo: e.target.value })}
                            />
                          </>
                        )}
                        {row.op === "in" && (
                          <textarea
                            placeholder="значения через запятую"
                            value={row.valueIn}
                            onChange={(e) => patchFilter(row.uid, { valueIn: e.target.value })}
                          />
                        )}
                        {row.op &&
                          row.op !== "is_null" &&
                          row.op !== "is_not_null" &&
                          row.op !== "between" &&
                          row.op !== "in" &&
                          row.op !== "json_path_exists" &&
                          !isJsonOp &&
                          (col?.type === "boolean" || col?.type === "bool" ? (
                            <select
                              value={row.valueSingle}
                              onChange={(e) => patchFilter(row.uid, { valueSingle: e.target.value })}
                            >
                              <option value="">—</option>
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              placeholder="значение"
                              value={row.valueSingle}
                              onChange={(e) => patchFilter(row.uid, { valueSingle: e.target.value })}
                            />
                          ))}
                        {isJsonOp && row.op !== "json_path_exists" && (
                          <input
                            type="text"
                            placeholder="значение для JSON-фильтра"
                            value={row.valueSingle}
                            onChange={(e) => patchFilter(row.uid, { valueSingle: e.target.value })}
                          />
                        )}
                        <button
                          type="button"
                          className="table-reports__btn table-reports__btn--danger"
                          onClick={() => removeFilter(row.uid)}
                        >
                          Удалить
                        </button>
                      </div>
                    );
                  })}
                  <button type="button" className="table-reports__btn" onClick={addFilter}>
                    + Фильтр
                  </button>
                </section>

                <section className="table-reports__section">
                  <h4>Сортировка</h4>
                  {block.sortRows.map((s) => (
                    <div key={s.uid} className="table-reports__row">
                      <select value={s.field} onChange={(e) => patchSort(s.uid, { field: e.target.value })}>
                        <option value="">поле…</option>
                        {schema.columns
                          ?.filter((c) => c.sortable)
                          .map((c) => (
                            <option key={c.name} value={c.name}>
                              {c.label || c.name}
                            </option>
                          ))}
                      </select>
                      <select value={s.direction} onChange={(e) => patchSort(s.uid, { direction: e.target.value })}>
                        <option value="asc">по возрастанию</option>
                        <option value="desc">по убыванию</option>
                      </select>
                      <button type="button" className="table-reports__btn" onClick={() => removeSort(s.uid)}>
                        Удалить
                      </button>
                    </div>
                  ))}
                  <button type="button" className="table-reports__btn" onClick={addSort}>
                    + Сортировка
                  </button>
                </section>

                <div className="table-reports__toolbar">
                  <div className="table-reports__field">
                    <label>Лимит строк (preview)</label>
                    <input
                      type="number"
                      min={1}
                      max={5000}
                      value={block.limit}
                      onChange={(e) =>
                        updateBlock(block.id, { limit: Math.max(1, parseInt(e.target.value, 10) || 50) })
                      }
                    />
                  </div>
                  <button
                    type="button"
                    className="table-reports__btn table-reports__btn--primary"
                    disabled={block.loadingPreview}
                    onClick={() => runPreviewBlock(block, 0)}
                  >
                    {block.loadingPreview ? "Загрузка…" : "Предпросмотр этой таблицы"}
                  </button>
                </div>

                {preview && (
                  <>
                    <div className="table-reports__meta">
                      Всего: {preview.total ?? "—"} · строк: {previewRows.length} · offset {preview.offset ?? block.offset}
                    </div>
                    {previewRows.length === 0 ? (
                      <div className="table-reports__empty">По заданным условиям данных не найдено.</div>
                    ) : (
                      <div className="table-reports__table-wrap">
                        <table>
                          <thead>
                            <tr>
                              {previewColumns.map((c) => (
                                <th key={c}>{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewRows.map((r, i) => (
                              <tr key={i}>
                                {previewColumns.map((c) => (
                                  <td key={c}>{r[c] != null ? String(r[c]) : ""}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="table-reports__toolbar">
                      <button
                        type="button"
                        className="table-reports__btn"
                        disabled={block.loadingPreview || block.offset <= 0}
                        onClick={() =>
                          runPreviewBlock(block, Math.max(0, block.offset - block.limit))
                        }
                      >
                        Назад
                      </button>
                      <button
                        type="button"
                        className="table-reports__btn"
                        disabled={
                          block.loadingPreview ||
                          (typeof preview.total === "number" &&
                            block.offset + block.limit >= preview.total)
                        }
                        onClick={() => runPreviewBlock(block, block.offset + block.limit)}
                      >
                        Вперёд
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        );
      })}

      <section className="table-reports__section">
        <h3>Общий предпросмотр и выгрузка</h3>
        <div className="table-reports__toolbar">
          <button type="button" className="table-reports__btn table-reports__btn--primary" onClick={previewAll}>
            Предпросмотр всех таблиц
          </button>
        </div>
        <div className="table-reports__toolbar">
          <div className="table-reports__field">
            <label htmlFor="tr-title">Заголовок в файле</label>
            <input id="tr-title" value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} />
          </div>
          <div className="table-reports__field">
            <label htmlFor="tr-trunc">Обрезка ячейки</label>
            <input
              id="tr-trunc"
              type="number"
              min={10}
              value={truncateCell}
              onChange={(e) => setTruncateCell(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="table-reports__btn table-reports__btn--primary"
            disabled={loadingRender}
            onClick={() => downloadRenderAll("pdf")}
          >
            PDF (все таблицы)
          </button>
          <button
            type="button"
            className="table-reports__btn table-reports__btn--primary"
            disabled={loadingRender}
            onClick={() => downloadRenderAll("docx")}
          >
            DOCX (все таблицы)
          </button>
        </div>
      </section>
    </div>
  );
}

export default TableReportBlocksPanel;
